import { figOutline } from './figureOutline';
import { box, canvas, ctx2d, ell, line } from './pixel';

/**
 * นกอินทรีทะยานฟ้า, drawn in code as a pixel-art sprite: a great eagle seen from above and to the
 * side, wings spread and beating — layered coverts, long dark flight feathers splayed into
 * fingers at the tips, a white head with a scowling golden eye and hooked beak, a fanned white
 * tail and yellow talons. Faces right; mirror for left. กวางเมฆหิมะ (the title's black eagle) is
 * the same bird drawn in a black palette.
 */
export interface EagleSprite {
  frames: HTMLCanvasElement[];
  /** Where the rider stands (centre of the back). */
  ax: number;
  ay: number;
}

const W = 132;
const H = 124;

/** The eagle's colours: the brown top-up eagle, or the black eagle of เถาฉางเยี่ย. */
export type EaglePalette = 'brown' | 'black';

const BROWN = {
  dark: '#2a1a0e',
  body: '#4a2e18',
  mid: '#6a4424',
  light: '#8e6036',
  gold: '#b88a4a',
  flight: '#20160e',
  flightL: '#3a2a1c',
  white: '#f4f1ea',
  whiteS: '#c8c2b6',
  beak: '#f0a818',
  beakS: '#b87810',
  eye: '#f2c020',
  talon: '#e8b830',
};

/** Ink black with a cold blue sheen on the coverts; the golden eye, beak and talons stay. */
const BLACK: typeof BROWN = {
  dark: '#0a0a10',
  body: '#16161e',
  mid: '#22222e',
  light: '#363a4c',
  gold: '#566078',
  flight: '#060609',
  flightL: '#1c1c28',
  white: '#3a3a48',
  whiteS: '#262632',
  beak: '#f0a818',
  beakS: '#b87810',
  eye: '#f2c020',
  talon: '#e8b830',
};

/** The palette being drawn (set while one bird's frames are built). */
let C = BROWN;
const withPalette = <T>(p: EaglePalette, draw: () => T): T => {
  const was = C;
  C = p === 'black' ? BLACK : BROWN;
  try {
    return draw();
  } finally {
    C = was;
  }
};

/**
 * One wing seen side-on, in the same chunky flat style as the pet fox: a solid wing shape from
 * the shoulder to the tip, a light band along the leading edge, dark primaries splayed into
 * fingers at the end. `lift` 1 = raised high, 0 = level, below 0 = beaten down.
 */
function wing(g: CanvasRenderingContext2D, sx: number, sy: number, lift: number, far: boolean): void {
  const span = 46;
  const up = lift >= 0 ? 1 : -1;
  const tx = sx + 10;
  const ty = sy - span * lift;
  const col = (c: string) => (far ? shadeHex(c, 0.62) : c);
  const P = (f: number, back: number): [number, number] => [
    sx + (tx - sx) * f - back,
    sy + (ty - sy) * f + up * back * 0.25,
  ];
  const fill = (pts: [number, number][], c: string) => {
    g.fillStyle = col(c);
    g.beginPath();
    pts.forEach(([x, y], k) => (k ? g.lineTo(x, y) : g.moveTo(x, y)));
    g.closePath();
    g.fill();
  };
  // Whole wing: leading edge out to the tip, then back along a fingered trailing edge.
  const trail: [number, number][] = [];
  for (let k = 0; k <= 10; k++) {
    const f = 1 - k / 10;
    const finger = f > 0.55 ? (k % 2 ? 4 : 0) : 0;
    trail.push(P(f, 20 + f * 6 - finger));
  }
  fill([P(0, 0), P(1, 0), ...trail, P(0, 14)], C.mid);
  // Dark primaries over the outer half.
  fill([P(0.55, 0), P(1, 0), ...trail.slice(0, 6)], C.flight);
  // Light coverts band along the leading edge.
  fill([P(0, 0), P(0.6, 0), P(0.6, 7), P(0, 8)], C.light);
  for (let k = 1; k < 6; k++) {
    const [x, y] = P(k / 10, 4);
    g.fillStyle = col(C.gold);
    g.fillRect(Math.round(x), Math.round(y), 2, 1);
  }
}

function shadeHex(hex: string, k: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.round(v * k);
  return `rgb(${ch(n >> 16)},${ch((n >> 8) & 255)},${ch(n & 255)})`;
}

function frame(lift: number): HTMLCanvasElement {
  const c = canvas(W, H);
  const g = ctx2d(c);
  const cx = 60;
  const cy = 66;
  // Far wing behind the body, a beat behind the near one.
  wing(g, cx + 6, cy - 7, Math.min(1, lift * 0.9 + 0.2), true);
  // Fanned white tail.
  for (let k = -3; k <= 3; k++) line(g, cx - 18, cy + 2, cx - 34, cy + 2 + k * 3, k % 2 ? C.white : C.whiteS);
  box(g, cx - 35, cy - 7, 3, 18, C.whiteS);
  // Body: deep brown with scalloped feathers, lighter on the back.
  ell(g, cx, cy, 22, 10, C.body);
  ell(g, cx - 1, cy - 3, 18, 6, C.mid);
  for (let k = 0; k < 7; k++) ell(g, cx - 12 + k * 4, cy - 3 + (k % 2), 2.2, 1.6, C.light);
  ell(g, cx + 2, cy + 6, 14, 4, C.dark);
  // Talons tucked under the belly.
  for (const dx of [-4, 4]) {
    box(g, cx + dx, cy + 9, 3, 4, C.talon);
    box(g, cx + dx - 1, cy + 13, 5, 1, '#141010');
  }
  // White head thrust forward, scowling golden eye, hooked beak.
  const hx = cx + 26;
  const hy = cy - 9;
  ell(g, cx + 19, cy - 6, 6, 6, C.white);
  ell(g, hx, hy, 8, 6.5, C.white);
  ell(g, hx - 2, hy + 3, 6, 3, C.whiteS);
  box(g, hx + 1, hy - 3, 4, 3, C.eye);
  box(g, hx + 3, hy - 2, 1, 1, '#000000');
  line(g, hx - 1, hy - 5, hx + 6, hy - 3, C.dark); // heavy brow
  box(g, hx + 6, hy - 1, 5, 4, C.beak);
  box(g, hx + 10, hy + 2, 2, 3, C.beakS);
  box(g, hx + 11, hy + 4, 1, 1, '#2a1a0a');
  box(g, hx + 6, hy + 2, 4, 1, C.beakS);
  // Near wing in front.
  wing(g, cx + 2, cy - 4, lift, false);
  // Hard pixel edges like the rest of the sprites.
  const img = g.getImageData(0, 0, W, H);
  for (let i = 3; i < img.data.length; i += 4) img.data[i] = img.data[i]! >= 110 ? 255 : 0;
  g.putImageData(img, 0, 0);
  return figOutline(c, '#120a06');
}

/**
 * A wing spread out to one side, as seen from in front or behind: a broad shape from the shoulder
 * to the tip (raised or lowered by `lift`, shortened by `reach` when it is turned away), rows of
 * scalloped coverts, long dark primaries splayed into fingers, and a pale leading edge.
 */
function spreadWing(
  g: CanvasRenderingContext2D,
  sx: number,
  sy: number,
  dir: number,
  lift: number,
  reach = 1,
  far = false,
): void {
  const span = 56 * reach;
  const tx = sx + dir * span;
  const ty = sy - lift * 24 * reach;
  const col = (c: string) => (far ? shadeHex(c, 0.72) : c);
  const P = (f: number, down: number): [number, number] => [sx + (tx - sx) * f, sy + (ty - sy) * f + down];
  const fill = (pts: [number, number][], c: string) => {
    g.fillStyle = col(c);
    g.beginPath();
    pts.forEach(([x, y], k) => (k ? g.lineTo(x, y) : g.moveTo(x, y)));
    g.closePath();
    g.fill();
  };
  const trail: [number, number][] = [];
  for (let k = 0; k <= 12; k++) {
    const f = 1 - k / 12;
    const finger = f > 0.62 ? (k % 2 ? 6 : 0) : 0;
    trail.push(P(f, 18 - f * 5 + finger));
  }
  fill([P(0, -3), P(1, 0), ...trail, P(0, 16)], C.mid);
  // Primaries: dark with a lighter shaft each.
  fill([P(0.58, 0), P(1, 0), ...trail.slice(0, 6)], C.flight);
  for (let k = 0; k < 6; k++) {
    const f = 0.62 + k * 0.065;
    const [x0, y0] = P(f, 1);
    const [x1, y1] = P(f - 0.02, 14 - f * 4);
    g.strokeStyle = col(C.flightL);
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(x0, y0);
    g.lineTo(x1, y1);
    g.stroke();
  }
  // Secondaries: a darker band along the trailing edge of the inner wing.
  fill([P(0, 11), P(0.6, 10), P(0.6, 15), P(0, 16)], C.body);
  // Coverts: three rows of scallops, light at the front.
  for (let row = 0; row < 3; row++)
    for (let k = 0; k <= 9; k++) {
      const f = 0.04 + (k / 9) * (0.6 - row * 0.12);
      const [x, y] = P(f, 1 + row * 3.4);
      g.fillStyle = col(row === 0 ? C.light : row === 1 ? C.gold : C.mid);
      g.beginPath();
      g.arc(x, y, 2.1, 0, Math.PI);
      g.fill();
    }
  // Pale leading edge.
  g.strokeStyle = col(C.gold);
  g.lineWidth = 1.5;
  g.beginPath();
  g.moveTo(...P(0, -3));
  g.lineTo(...P(1, 0));
  g.stroke();
}

/**
 * The eagle seen from in front (`toward`) or behind, turned `yaw` (0 = straight on, 0.6 = three-
 * quarter, head to the right): the near wing stretches out, the far one is foreshortened.
 */
function frontBack(lift: number, toward: boolean, yaw = 0): HTMLCanvasElement {
  const c = canvas(W, H);
  const g = ctx2d(c);
  const cx = 66;
  const cy = 66;
  const hx = Math.round(cx + yaw * 12);
  const tailX = Math.round(cx - yaw * 12);
  // Wings: the one on the head's side is nearer the viewer when coming toward us.
  const nearDir = toward ? 1 : -1;
  const wing = (dir: number) =>
    spreadWing(
      g,
      cx + dir * 6,
      cy - (toward ? 4 : 6),
      dir,
      lift,
      dir === nearDir ? 1 : 1 - yaw * 0.55,
      dir !== nearDir && yaw > 0,
    );
  if (!toward) {
    // From behind: head peeking over the back, the fanned tail nearest.
    ell(g, hx, cy - 14, 6, 5, C.white);
    wing(-nearDir);
    wing(nearDir);
    ell(g, cx, cy, 11 - yaw * 2, 13, C.body);
    ell(g, cx, cy - 4, 8 - yaw * 2, 8, C.mid);
    for (let k = 0; k < 5; k++) ell(g, cx - 6 + k * 3, cy - 4 + (k % 2) * 2, 2, 1.6, C.light);
    for (let k = -3; k <= 3; k++) line(g, tailX, cy + 10, tailX + k * 3, cy + 24, k % 2 ? C.white : C.whiteS);
    box(g, tailX - 10, cy + 23, 21, 3, C.whiteS);
  } else {
    for (let k = -3; k <= 3; k++)
      line(g, tailX, cy + 4, tailX + k * 3 - 6, cy - 8, k % 2 ? C.white : C.whiteS);
    wing(-nearDir);
    wing(nearDir);
    ell(g, cx, cy + 2, 11 - yaw * 2, 12, C.body);
    ell(g, cx, cy + 4, 7, 8, C.dark);
    for (let k = 0; k < 4; k++) ell(g, cx - 4 + k * 3, cy + 1 + (k % 2) * 3, 1.6, 1.2, C.mid);
    for (const dx of [-4, 3]) {
      box(g, cx + dx, cy + 13, 3, 4, C.talon);
      box(g, cx + dx - 1, cy + 17, 5, 1, '#141010');
    }
    // White head with a fierce brow, golden eyes and hooked beak, turned by yaw.
    ell(g, hx, cy - 10, 8, 7.5, C.white);
    ell(g, hx, cy - 6, 6, 3, C.whiteS);
    const eyes = yaw > 0 ? [hx - 1, hx + 4] : [hx - 4, hx + 2];
    for (const ex of eyes) {
      box(g, ex, cy - 13, 3, 2, C.eye);
      box(g, ex + 1, cy - 13, 1, 1, '#000000');
    }
    line(g, eyes[0]! - 2, cy - 16, eyes[0]! + 3, cy - 14, C.dark);
    line(g, eyes[1]! + 4, cy - 16, eyes[1]! - 1, cy - 14, C.dark);
    const bx = hx + Math.round(yaw * 6);
    box(g, bx - 2, cy - 10, 4, 4, C.beak);
    box(g, bx - 1 + Math.round(yaw * 2), cy - 6, 2, 3, C.beakS);
    box(g, bx - 2, cy - 10, 4, 1, '#ffd060');
  }
  const img = g.getImageData(0, 0, W, H);
  for (let i = 3; i < img.data.length; i += 4) img.data[i] = img.data[i]! >= 110 ? 255 : 0;
  g.putImageData(img, 0, 0);
  return figOutline(c, '#120a06');
}

export interface EagleViews {
  side: EagleSprite;
  front: EagleSprite;
  back: EagleSprite;
  /** Three-quarter views (head to the right). */
  front3: EagleSprite;
  back3: EagleSprite;
}

const views: Partial<Record<EaglePalette, EagleViews>> = {};
const BEAT = [1, 0.55, 0.05, -0.45, -0.1, 0.5];

/** The eagle in each view, each with its six-frame wingbeat. */
export function eagleViews(palette: EaglePalette = 'brown'): EagleViews {
  return (views[palette] ??= withPalette(palette, () => ({
    side: eagleSprite(palette),
    front: { frames: BEAT.map((l) => frontBack(l, true)), ax: 66, ay: 58 },
    back: { frames: BEAT.map((l) => frontBack(l, false)), ax: 66, ay: 56 },
    front3: { frames: BEAT.map((l) => frontBack(l, true, 0.6)), ax: 66, ay: 58 },
    back3: { frames: BEAT.map((l) => frontBack(l, false, 0.6)), ax: 66, ay: 56 },
  })));
}

const cached: Partial<Record<EaglePalette, EagleSprite>> = {};

/** The eagle's wingbeat: six frames from raised to beaten down and back. */
export function eagleSprite(palette: EaglePalette = 'brown'): EagleSprite {
  return (cached[palette] ??= withPalette(palette, () => ({ frames: BEAT.map(frame), ax: 58, ay: 57 })));
}
